// src/components/Login.js
import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../commonLogin.css";
import Toast from "../../utils/toast";
import Loader from "../Loader";
import createInstance from "../../api/axiosinstance";
import { useDispatch, useSelector } from "react-redux";
import {
  loginSuccess,
  setLoading,
  setError,
  logout,
  loginAdmin,
  loginOrgUser,
  checkUserType,
  requestCandidateOtp,
  getAllTPInterviewers,
  loginTPUser,
} from "../../Redux/slices/authSlice";
import {
  Button,
  FormControl,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Paper,
  Avatar,
  Divider,
  Fade,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@mui/material";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    backgroundColor: "#ffffff",

    "& fieldset": {
      borderColor: "rgb(37, 99, 235)",
    },

    "&:hover fieldset": {
      borderColor: "rgb(37, 99, 235)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "rgb(37, 99, 235)",
      borderWidth: 2,
    },
  },

  /* 🔑 THIS IS THE KEY PART */
  "& input": {
    color: "#111827",
    padding: "14px",

    "&::placeholder": {
      color: "black",
      opacity: 1, // IMPORTANT: browsers lower this by default
      fontWeight: 500,
    },

    "&::-webkit-input-placeholder": {
      color: "black",
      opacity: 1,
    },

    "&::-moz-placeholder": {
      color: "rgb(37, 99, 235)",
      opacity: 1,
    },

    "&:-ms-input-placeholder": {
      color: "rgb(37, 99, 235)",
    },
  },

  "& .MuiInputAdornment-root i": {
    color: "rgb(37, 99, 235)",
  },

  "& .MuiFormHelperText-root": {
    color: "#dc2626",
    fontSize: "12px",
  },
};

const Login = () => {
  const dispatch = useDispatch();
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const { isAuthenticated, loading, error, userType, user } = useSelector(
    (state) => state.auth,
  );
  const passwordRef = useRef(null);
  const [formData, setFormData] = useState({ email: "" });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    role: "",
    organization: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dishaApi = createInstance("DISHA");
  const [step, setStep] = useState("email");

  useEffect(() => {
    if (isAuthenticated && user) {
      const route = user.role?.toLowerCase() + "Landing";
      navigate("/" + route);
    }
  }, [isAuthenticated, user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateEmail = () => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      setErrors({ ...errors, email: "Please enter a valid email address" });
      return false;
    }
    return true;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;

    dispatch(setLoading(true));
    try {
      const result = await dispatch(checkUserType(formData.email)).unwrap();
      if (result.role === "candidate") {
        // Candidate: Request OTP
        await dispatch(requestCandidateOtp(formData.email)).unwrap();
        navigate("/otpVerification", {
          state: { email: formData.email, source: "candidateLogin" },
        });
        Toast.success("OTP sent successfully");
      } else {
        // Admin, OrgAdmin, Interviewer: Show password field
        setStep("password");
        Toast.info("Please enter your password");
      }
    } catch (err) {
      Toast.error(err || "User not found");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const passwordValue = passwordRef.current?.value || "";
    if (!passwordValue) {
      setErrors({ ...errors, password: "Please enter a password" });
      return;
    }

    dispatch(setLoading(true));
    try {
      let userData;

      if (userType === "Admin") {
        userData = await dispatch(
          loginAdmin({ email: formData.email, password: passwordValue }),
        ).unwrap();
      } else if (["OrgAdmin", "Interviewer", "HR"].includes(userType)) {
        userData = await dispatch(
          loginOrgUser({ email: formData.email, password: passwordValue }),
        ).unwrap();
      } else if (userType === "TP-Interviewer") {
        // ✅ Step 1: Get all interviewers
        const allInterviewers = await dispatch(getAllTPInterviewers()).unwrap();
        const matchedUser = allInterviewers.find(
          (u) => u.email === formData.email,
        );

        if (matchedUser && matchedUser.activation_status === "Pending") {
          // ✅ Step 2: Show popup and stop login
          setShowVerificationPopup(true);
          dispatch(setLoading(false));
          return;
        }

        // ✅ Step 3: Proceed to login if active
        userData = await dispatch(
          loginTPUser({ email: formData.email, password: passwordValue }),
        ).unwrap();
      }

      // 🔒 Check activation status after login
      if (userData?.activation_status === "Pending") {
        setShowVerificationPopup(true);
        dispatch(setLoading(false));
        return;
      }

      if (userData?.status === "Active") {
        navigate(`/${userData.role.toLowerCase()}Landing`);
        console.log("dddd", userData.role);
      } else {
        navigate("/changePassword");
      }

      Toast.success("Logged in successfully");
    } catch (err) {
      Toast.error("Invalid credentials");
      console.log("errrr", err);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const goBack = () => {
    setStep("email");
    setErrors({ ...errors, password: "" });
  };

  return (
    <Fade in={true} timeout={800}>
      <Box>
        <Box sx={{ width: "100%", maxWidth: 420, padding: "20px" }}>
          <Box
            sx={{
              textAlign: "center",
              mb: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mb: 2,
                // background: "linear-gradient(135deg, #e59d02, #b37a01)",
                background: "#334155",
                // boxShadow: "0 8px 24px rgba(229,157,2,0.4)",
              }}
            >
              <i className="bi bi-person-circle" style={{ fontSize: 28 }}></i>
            </Avatar>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: "#334155",
                letterSpacing: "0.4px",
              }}
            >
              {step === "email" ? "Sign In" : "Enter Password"}
            </Typography>

            <Typography
              sx={inputStyles}
            >
              {step === "email"
                ? "Enter your email to continue to Strategic Hire"
                : `Welcome back, let's verify it's you`}
            </Typography>
          </Box>

          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
            onSubmit={
              step === "email" ? handleEmailSubmit : handlePasswordSubmit
            }
          >
            {step === "email" ? (
              <TextField
                type="email"
                name="email"
                placeholder="Email Address"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                fullWidth
                size="medium"
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i
                        className="bi bi-envelope"
                        style={{ color: "rgb(37, 99, 235)" }}
                      ></i>
                    </InputAdornment>
                  ),
                }}
                sx={inputStyles}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 2,
                    backgroundColor: "rgb(37, 99, 235)",
                    borderRadius: "12px",
                    mb: 2,
                    cursor: "pointer",
                  }}
                  onClick={goBack}
                >
                  <i
                    className="bi bi-envelope"
                    style={{ color: "#000000", marginRight: "10px" }}
                  ></i>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ color: "#000000" }}>
                      {formData.email}
                    </Typography>
                  </Box>
                  <i className="bi bi-pencil" style={{ color: "#000000" }}></i>
                </Box>

                <TextField
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  inputRef={passwordRef}
                  autoFocus
                  fullWidth
                  variant="outlined"
                  size="medium"
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <i
                          className="bi bi-lock"
                          style={{ color: "rgb(37, 99, 235)" }}
                        ></i>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          sx={{ color: "#00a2a2" }}
                        >
                          {showPassword ? (
                            <i className="bi bi-eye"></i>
                          ) : (
                            <i className="bi bi-eye-slash"></i>
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyles}
                />
              </>
            )}

            {step === "password" && (
              <Box sx={{ textAlign: "right", mt: -2 }}>
                <Link to="/forgotPassword" className="text-decoration-none">
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgb(37, 99, 235)",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Box>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                borderRadius: "12px",
                padding: "12px",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: "600",
                letterSpacing: "0.5px",
                // background: "linear-gradient(135deg, #e59d02, #b37a01)",
                background: "rgb(37, 99, 235)",
                boxShadow: "0 4px 10px rgba(0, 162, 162, 0.3)",
                "&:hover": {
                  background: "#334155",
                  boxShadow: "0 6px 15px rgba(0, 162, 162, 0.4)",
                },
                mt: 2,
                color:"#ffffff"
              }}
            >
              {loading ? (
                <Loader />
              ) : (
                <>
                  {step === "email" ? "Continue" : "Sign In"}
                  <i
                    className="bi bi-arrow-right"
                    style={{ marginLeft: "8px" }}
                  ></i>
                </>
              )}
            </Button>
          </Box>

          {error && (
            <Typography
              variant="body2"
              sx={{
                color: "#ff5252",
                mt: 2,
                textAlign: "center",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="bi bi-exclamation-circle"
                style={{ marginRight: "6px" }}
              ></i>
              {error}
            </Typography>
          )}

          {/* <Box sx={{ mt: 4, textAlign: "center" }}>
          <Divider sx={{ 
            "&::before, &::after": {
              borderColor: "rgba(255, 255, 255, 0.2)",
            }
          }}>
            <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.5)" }}>
              New to Strategic Hire?
            </Typography>
          </Divider>
          
          <Link to="/register" className="text-decoration-none">
            <Button 
              fullWidth
              sx={{
                mt: 2,
                borderRadius: "12px",
                padding: "10px",
                textTransform: "none",
                fontSize: "1rem",
                border: "2px solid rgba(0, 224, 255, 0.3)",
                color: "#00e0ff",
                "&:hover": {
                  backgroundColor: "rgba(0, 224, 255, 0.1)",
                  border: "2px solid rgba(0, 224, 255, 0.6)",
                }
              }}
            >
              Create an Account
            </Button>
          </Link>
        </Box> */}
        </Box>
        {/* Verification Popup */}
        <Dialog
          open={showVerificationPopup}
          onClose={() => setShowVerificationPopup(false)}
          PaperProps={{
            sx: {
              borderRadius: 4,
              backgroundColor: "rgba(0, 106, 113, 0.95)",
              color: "white",
              padding: 2,
              backdropFilter: "blur(10px)",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              fontWeight: 700,
              backgroundImage: "linear-gradient(135deg, #00e0ff, #00a2a2)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Account Under Verification
          </DialogTitle>

          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                color: "rgba(255,255,255,0.85)",
                mt: 1,
              }}
            >
              Your account is currently under verification. Please come back
              later once it’s approved.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button
              onClick={() => {
                setShowVerificationPopup(false);
                navigate("/");
              }}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                background: "linear-gradient(135deg, #00e0ff, #00a2a2)",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  background: "linear-gradient(135deg, #00d1ee, #009191)",
                },
              }}
            >
              Home
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default Login;
